import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, ArrowLeft, RefreshCw, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { cn } from '../utils/cn';

const EmailConfigPage = () => {
  const navigate = useNavigate();
  const [checkingConfig, setCheckingConfig] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailConfig, setEmailConfig] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);

  useEffect(() => {
    checkEmailConfig();
  }, []);

  const checkEmailConfig = async () => {
    setCheckingConfig(true);
    setError(null);
    
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('認証エラーが発生しました');
      }

      if (!sessionData.session) {
        throw new Error('ログインが必要です');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-email-delivery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.details || 'メール設定の確認に失敗しました');
      }
      
      setEmailConfig(data.emailConfig || { provider: 'none', enabled: false, sender: 'none' });
      setLogs(data.recentLogs || []);
      
      if (!data.emailConfig?.enabled) {
        setError('メール送信が有効になっていません。Supabaseのダッシュボードで設定を確認してください。');
      }
    } catch (err) {
      console.error('Error checking email config:', err);
      setError(err instanceof Error ? err.message : 'メール設定の確認に失敗しました');
      setEmailConfig({ provider: 'none', enabled: false, sender: 'none' });
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendingTest(true);
    setError(null);
    setSuccess(null);
    
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('認証エラーが発生しました');
      }

      if (!sessionData.session) {
        throw new Error('ログインが必要です');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.details || 'テストメールの送信に失敗しました');
      }
      
      setSuccess(data.message || 'テストメールを送信しました。メールボックスを確認してください。');
      setEmail('');
      
      // 設定を再確認
      setTimeout(() => {
        checkEmailConfig();
      }, 2000);
    } catch (err) {
      console.error('Error sending test email:', err);
      setError(err instanceof Error ? err.message : 'テストメールの送信に失敗しました');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-primary-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                メール設定確認
              </h2>
            </div>
            <button
              onClick={checkEmailConfig}
              disabled={checkingConfig}
              className="btn btn-sm btn-secondary flex items-center"
            >
              <RefreshCw className={cn("w-4 h-4 mr-1", checkingConfig && "animate-spin")} />
              <span>{checkingConfig ? '確認中...' : '再確認'}</span>
            </button>
          </div>

          {checkingConfig ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {emailConfig && (
                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">現在の設定</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">プロバイダー</p>
                      <p className="font-medium">{emailConfig.provider || 'なし'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">有効状態</p>
                      <p className={cn(
                        "font-medium",
                        emailConfig.enabled ? "text-green-600" : "text-red-600"
                      )}>
                        {emailConfig.enabled ? '有効' : '無効'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">送信者</p>
                      <p className="font-medium">{emailConfig.sender || 'なし'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">SMTP接続状態</p>
                      <p className={cn(
                        "font-medium",
                        emailConfig.smtpStatus === 'connected' ? "text-green-600" : 
                        emailConfig.smtpStatus === 'failed' ? "text-red-600" : "text-yellow-600"
                      )}>
                        {emailConfig.smtpStatus === 'connected' ? '接続成功' : 
                         emailConfig.smtpStatus === 'failed' ? '接続失敗' : '不明'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">テストメール送信</h3>
                <form onSubmit={handleSendTestEmail} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      メールアドレス
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="example@domain.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sendingTest}
                    className={cn(
                      "w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
                      sendingTest && "opacity-70 cursor-not-allowed"
                    )}
                  >
                    {sendingTest ? 'メール送信中...' : 'テストメールを送信'}
                  </button>
                </form>
              </div>

              {logs.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">最近のログ</h3>
                  <div className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-60">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left py-2">日時</th>
                          <th className="text-left py-2">イベント</th>
                          <th className="text-left py-2">ステータス</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="py-2">{new Date(log.created_at).toLocaleString()}</td>
                            <td className="py-2">{log.event_type}</td>
                            <td className="py-2">{log.status || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="rounded-md bg-green-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">{success}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  ログインページに戻る
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfigPage;